import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getDeviceId } from '../lib/device';
import { saveOffline, loadOffline, clearOffline } from '../lib/offline';
import { useLicense } from '../lib/license';
import { MODULE_ADDON } from '../lib/permissions';
import { restoreStaff, isRestoring } from '../lib/firmAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const { hasAddon } = useLicense();

  // Oturum sunucuda sonlandırıldıysa (ör. cihaz kaldırıldı) girişe yönlendir
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      const url = error.config?.url || '';
      if (error.response?.status === 401 && localStorage.getItem('token') && !url.includes('/auth/login')) {
        if (restoreStaff(error.response.data?.detail || 'Firma erişimi sona erdi')) return Promise.reject(error);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        sessionStorage.setItem('auth-message', error.response.data?.detail || 'Oturumunuz sona erdi');
        window.location.assign('/login');
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data.role !== 'firma_yetkilisi') saveOffline('user', response.data);
    } catch (error) {
      // Bağlantı yoksa (admin / İSG) cihazdaki kullanıcı ile çevrimdışı devam et
      const cached = !error.response ? await loadOffline('user') : null;
      if (cached) setUser(cached);
      else if (error.response && error.response.status !== 402) logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, device_id: getDeviceId() });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const registerCompany = async (formData) => {
    const response = await axios.post(`${API}/auth/register-company`, formData);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // İnternet geri gelince oturum bilgisini tazele
  useEffect(() => {
    const on = () => { if (localStorage.getItem('token')) fetchUser(); };
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, []);

  const logout = () => {
    if (isRestoring()) return;
    clearOffline();
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Yetki kontrol fonksiyonları
  const hasPermission = (module, action) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const permissions = user.permissions || {};
    const modulePerms = permissions[module] || {};
    return modulePerms[action] === true;
  };

  const addonOn = (module) => !MODULE_ADDON[module] || hasAddon(MODULE_ADDON[module]);
  const canView = (module) => addonOn(module) && hasPermission(module, 'view');
  // Sayfa / sekme erişimi (Süper Admin her zaman; kapalı eklentide kimse)
  const canTab = (module, tab) => canView(module) && (user?.is_super_admin || user?.permissions?.[module]?.[`tab_${tab}`] === true);
  const canCreate = (module) => hasPermission(module, 'create');
  const canEdit = (module) => hasPermission(module, 'edit');
  const canDelete = (module) => hasPermission(module, 'delete');

  // Lokasyon yetkisi kontrolü
  const hasLocationAccess = (locationId) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    if (!user.allowed_locations || user.allowed_locations.length === 0) return true; // Boş liste = tüm lokasyonlar
    return user.allowed_locations.includes(locationId);
  };

  // Talep/belge iş akışı rolleri: firma oluşturur & yükler, İSG Uzmanı / Süper Admin inceler
  const isFirm = user?.role === 'firma_yetkilisi';
  const isReviewer = !!user && (user.is_super_admin || user.role === 'isg_expert');

  // Kullanıcı bilgilerini güncelle (örn: şifre değiştirme sonrası)
  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      registerCompany,
      logout,
      hasPermission,
      canView,
      canCreate,
      canEdit,
      canDelete,
      hasLocationAccess,
      isFirm,
      isReviewer,
      canTab,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
